import React, { useState, useEffect, useRef } from "react";
import Figure from "react-bootstrap/Figure";
import ProgressBar from "react-bootstrap/ProgressBar";
import "../assets/css/AppLoadingIndicator.css"; // Assuming this CSS exists and is correct

const AppLoadingIndicator = ({
  progress = 0, // Controlled by App.jsx state which follows loadingManager
  status = "Loading application...", // Controlled by App.jsx state
  isVisible = true, // Controlled by App.jsx loading state
  onComplete = () => {}, // Called when fade-out finishes
  minDisplayTime = 1000, // Minimum time the indicator should be visible
}) => {
  const [displayProgress, setDisplayProgress] = useState(0);
  const [startTime] = useState(Date.now()); // Track when the component mounted

  // Style for fading out
  const [style, setStyle] = useState({
    opacity: 1,
    transition: "opacity 0.5s ease-out", // Use ease-out for fade-out
    pointerEvents: "auto", // Initially allow interactions (though usually covers whole screen)
    visibility: "visible", // Control visibility explicitly
  });

  // Refs to manage state across renders without causing re-renders
  const isCompletingRef = useRef(false); // Track if the completion sequence has started
  const fadeTimeoutRef = useRef(null);
  const completeTimeoutRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Effect for animating progress bar smoothly
  useEffect(() => {
    // Only animate if the target progress is higher than the displayed progress
    if (progress > displayProgress) {
      const animate = () => {
        setDisplayProgress((prev) => {
          // If component is unmounted or progress target changes mid-animation, stop
          if (prev >= progress) {
            cancelAnimationFrame(animationFrameRef.current);
            return prev;
          }

          const diff = progress - prev;
          // Adjust step for smoother animation: smaller steps when close
          const step = Math.max(1, Math.ceil(diff * 0.1)); // Slower animation step
          const next = Math.min(prev + step, progress);

          // Continue animation if not yet reached target
          if (next < progress) {
            animationFrameRef.current = requestAnimationFrame(animate);
          }
          return next;
        });
      };
      // Start the animation
      animationFrameRef.current = requestAnimationFrame(animate);
    } else if (progress < displayProgress) {
      // If progress goes down (e.g., reset), jump directly
      setDisplayProgress(progress);
    }

    // Cleanup function to cancel animation frame if component unmounts or progress changes
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [progress, displayProgress]); // Rerun effect if progress target or displayProgress changes

  // Effect for handling the completion sequence (fade out and call onComplete)
  useEffect(() => {
    // Check if progress reached 100% AND the completion process hasn't started yet
    if (progress >= 100 && !isCompletingRef.current) {
      // console.log(
      //   "AppLoadingIndicator: Progress reached 100%, starting completion sequence"
      // );
      isCompletingRef.current = true; // Mark completion as started

      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minDisplayTime - elapsedTime);
      const fadeDuration = 500; // Must match CSS transition duration
      const cleanupDelay = 100; // Small buffer after fade

      // console.log(
      //   `AppLoadingIndicator: Elapsed: ${elapsedTime}ms, MinDisplay: ${minDisplayTime}ms, RemainingWait: ${remainingTime}ms`
      // );

      // Clear any potentially lingering timeouts from previous renders
      clearTimeout(fadeTimeoutRef.current);
      clearTimeout(completeTimeoutRef.current);

      // Start fade-out after the remaining minimum display time
      fadeTimeoutRef.current = setTimeout(() => {
        // console.log("AppLoadingIndicator: Starting fade out");
        setStyle({
          opacity: 0,
          transition: `opacity ${fadeDuration}ms ease-out`,
          pointerEvents: "none", // Disable interactions during fade
          visibility: "hidden", // Hide after fade
        });

        // Set timeout to call onComplete *after* the fade transition finishes
        completeTimeoutRef.current = setTimeout(() => {
          // console.log(
          //   "AppLoadingIndicator: Fade complete, calling onComplete callback"
          // );
          onComplete(); // Signal completion to parent component (App.jsx)
        }, fadeDuration + cleanupDelay); // Wait for fade + buffer
      }, remainingTime);
    }

    // Cleanup function for timeouts when component unmounts or dependencies change
    return () => {
      clearTimeout(fadeTimeoutRef.current);
      clearTimeout(completeTimeoutRef.current);
    };
    // Dependencies: progress, startTime, minDisplayTime, onComplete
  }, [progress, startTime, minDisplayTime, onComplete]);

  // Effect for the failsafe timeout (optional but recommended)
  useEffect(() => {
    const failsafeDuration = 15000; // 15 seconds failsafe

    const failsafeTimer = setTimeout(() => {
      // Only trigger if still visible (loading) and completion hasn't started
      if (isVisible && !isCompletingRef.current) {
        console.warn(
          `AppLoadingIndicator: Failsafe triggered after ${
            failsafeDuration / 1000
          }s - forcing completion sequence`
        );
        isCompletingRef.current = true; // Mark as completing

        // Force fade out immediately
        setStyle({
          opacity: 0,
          transition: "opacity 0.5s ease-out",
          pointerEvents: "none",
          visibility: "hidden",
        });

        // Force completion callback after fade
        completeTimeoutRef.current = setTimeout(() => {
          // console.log("AppLoadingIndicator: Forced completion via failsafe");
          onComplete();
        }, 600); // Allow time for fade transition
      }
    }, failsafeDuration);

    // Cleanup failsafe timer on unmount or if isVisible changes
    return () => clearTimeout(failsafeTimer);
  }, [isVisible, onComplete]); // Rerun if visibility changes

  // If the parent component tells us not to be visible, don't render.
  // Note: The fade-out logic handles visual hiding *before* isVisible becomes false.
  if (!isVisible && style.opacity === 0) {
    return null;
  }

  // Render the loading indicator UI
  return (
    <div className="app-loading-container text-center" style={style}>
      <div className="app-loading-content text-center">
        <Figure className="mb-3 text-center">
          {/* Consider adding error handling or a placeholder if the image fails to load */}
          <Figure.Image
            src="/Q_logo.280_prog.jpeg"
            alt="Application Logo"
            className="app-loading-logo"
            width={140}
            height={140}
            // Add loading="eager" if this logo is critical for LCP
            loading="eager"
          />
          {/* Status message */}
          <Figure.Caption className="app-loading-status">
            {status || "Loading..."} {/* Provide a fallback status */}
          </Figure.Caption>
        </Figure>

        {/* Progress Bar */}
        <div className="app-loading-progress-wrapper">
          <ProgressBar
            now={displayProgress}
            // Animate only while loading, not at 100%
            animated={displayProgress < 100 && displayProgress > 0}
            // Change variant to success when complete
            variant={displayProgress >= 100 ? "success" : "info"}
            className="app-loading-progress-bar-rb"
          />
          {/* Display percentage text */}
          <div className="progress-text">{displayProgress}%</div>
        </div>
      </div>
    </div>
  );
};

export default AppLoadingIndicator;
